import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ExperienceDWS } from "@interfaces/dws/experience-dws";
import { PaymentLink } from "@interfaces/dws/payment";
import { ReservationDTO } from "@interfaces/dws/reservation";
import { Host } from "@interfaces/host";
import { WSPaymentsService, resolvePaymentLinkLocale } from "@services/dws";
import { locale } from "@util/locale";
import { tr } from "@util/tr";
import {
  openPaymentLinkEmail,
  openPaymentLinkWhatsApp,
  PaymentLinkSendRecipient,
  resolveSentToLabel
} from "@web/app/pages/payments/payment-link-send.util";
import { toast } from "@web/util/toast";

@Component({
  selector: 'web-payment-link-dialog',
  templateUrl: './payment-link-dialog.component.html',
  styleUrls: ['./payment-link-dialog.component.scss']
})
export class PaymentLinkDialogComponent implements OnInit {
  paymentLink!: PaymentLink;
  isPaid!: boolean;
  reservation!: ReservationDTO;
  experience!: ExperienceDWS;
  companyInt!: Host.Company;
  wineryId!: string;
  amountDueCents!: number;
  /** Role permission from reservation detail (canEdit). */
  private canRegeneratePermission = true;
  sendMenuOpen = false;
  sendActions: Array<{id: string, name: string}> = [];
  regenerating = false;

  get customerName() {
    return this.reservation?.reservationMasterContact?.name;
  }

  get customerEmail() {
    return this.reservation?.reservationMasterContact?.email;
  }

  get customerPhone() {
    return this.reservation?.reservationMasterContact?.phoneNumber;
  }

  get linkStatus(): string {
    if (this.isPaid || this.paymentLink?.linkStatus === 'paid') {
      return 'paid';
    }
    return this.paymentLink?.linkStatus || (this.paymentLink?.lastSentTo ? 'sent' : 'generated');
  }

  get canSend(): boolean {
    return !this.isPaid && this.linkStatus !== 'paid' && this.linkStatus !== 'expired';
  }

  get canRegenerate(): boolean {
    return this.canRegeneratePermission
      && !this.isPaid
      && this.linkStatus === 'expired'
      && this.amountDueCents > 0;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dlg: MatDialogRef<PaymentLinkDialogComponent>,
    private wsPaymentsService: WSPaymentsService
  ) {
    this.reservation = data.reservation;
    this.experience = data.experience;
    this.companyInt = data.companyInt;
    this.paymentLink = data.paymentLink;
    this.isPaid = data.isPaid || false;
    this.wineryId = data.wineryId || this.companyInt?.id;
    this.amountDueCents = data.amountDueCents ?? this.paymentLink?.amountCents ?? 0;
    this.canRegeneratePermission = data.canRegenerate !== false;
  }

  ngOnInit() {
    this.initSendActions();
  }

  private initSendActions() {
    this.sendActions = [
      { id: 'email', name: 'Email' },
      { id: 'whatsapp', name: 'Whatsapp' }
    ];
  }

  private getSendRecipient(): PaymentLinkSendRecipient {
    return {
      name: this.customerName,
      email: this.customerEmail,
      phone: this.customerPhone
    };
  }

  onSendActionSelected(action: {id: string, name: string}) {
    switch(action.id) {
      case 'email':
        this.sendMailTo();
        break;
      case 'whatsapp':
        this.sendWhatsApp();
        break;
    }
  }

  closeClicked() {
    this.dlg.close(this.paymentLink);
  }

  copyLinkToClipboard() {
    navigator.clipboard.writeText(this.paymentLink.url).then(() => {
      toast(tr('Copied to clipboard!'));
    });
  }

  regenerateLink() {
    if (!this.canRegenerate || this.regenerating) {
      return;
    }
    if (!this.wineryId) {
      toast(tr('Unable to generate payment link: winery information is missing'));
      return;
    }
    if (this.amountDueCents <= 0) {
      toast(tr('No amount due to collect'));
      return;
    }
    this.regenerating = true;
    this.wsPaymentsService.regeneratePaymentLink(this.paymentLink.id, {
      wineryId: this.wineryId,
      amountCents: this.amountDueCents,
      locale: resolvePaymentLinkLocale(this.reservation?.languageIso),
    }).subscribe({
      next: (link) => {
        this.paymentLink = link;
        this.isPaid = link.linkStatus === 'paid';
        this.regenerating = false;
        toast(tr('payments.paymentLink.regenerated'));
      },
      error: (err) => {
        this.regenerating = false;
        const reason = err?.error?.message || err?.error || err?.message;
        if (reason === 'payment_already_completed' || reason?.includes?.('payment_already_completed')) {
          toast(tr('Payment already completed'));
          return;
        }
        if (reason === 'no_amount_due' || reason?.includes?.('no_amount_due')) {
          toast(tr('No amount due to collect'));
          return;
        }
        if (reason === 'dwspay_not_configured' || reason?.includes?.('dwspay_not_configured')) {
          toast(tr('Payment cannot be generated. Check WS Payments configuration.'));
          return;
        }
        toast(tr('There was an error generating the payment link'));
      }
    });
  }

  sendWhatsApp() {
    const message = this.generateMessage();
    openPaymentLinkWhatsApp(this.customerPhone, message);
    const sentTo = resolveSentToLabel('whatsapp', this.getSendRecipient());
    this.wsPaymentsService.markPaymentLinkAsSent(this.paymentLink.id, sentTo).subscribe(
      () => {
        this.paymentLink.lastSentTo = sentTo;
        this.paymentLink.linkStatus = 'sent';
      },
      () => toast(tr('There was an error marking the payment link as sent'))
    );
  }

  sendMailTo() {
    const message = this.generateMessage();
    const subject = this.generateSubject();
    openPaymentLinkEmail(this.customerEmail, subject, message);
    const sentTo = resolveSentToLabel('email', this.getSendRecipient());
    this.wsPaymentsService.markPaymentLinkAsSent(this.paymentLink.id, sentTo).subscribe(
      () => {
        this.paymentLink.lastSentTo = sentTo;
        this.paymentLink.linkStatus = 'sent';
      },
      () => toast(tr('There was an error marking the payment link as sent'))
    );
  }

  generateMessage(): string {
    var messageOrig = tr("Contact_USER_payment_link");
    var name = this.reservation?.reservationMasterContact?.name;
    var date = this.reservation?.date;
    const inputDate = new Date(date!);
    const outputDateStr = `${inputDate
      .getDate()
      .toString()
      .padStart(2, "0")}-${(inputDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${inputDate.getFullYear()}`;
    var time = this.reservation?.time;
    var reservationId = this.reservation?.id;
    var cantina = this.companyInt?.name;
    var experience = this.experience?.details.titles.find(t => t.languageIsoCode.toLowerCase() == locale().locale)?.text || '';
    return this.formatString(
      messageOrig,
      name,
      outputDateStr,
      time,
      cantina,
      reservationId,
      experience,
      this.paymentLink.url,
      this.companyInt.company_email,
      this.companyInt.phone || tr('on our website')
    );
  }

  generateSubject(): string {
    var messageOrig = tr("Contact_USER_subj_payment_link");
    var name = this.reservation?.reservationMasterContact?.name;
    var reservationId = this.reservation?.id;
    var date = this.reservation?.date;
    const inputDate = new Date(date!);
    const outputDateStr = `${inputDate
      .getDate()
      .toString()
      .padStart(2, "0")}-${(inputDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${inputDate.getFullYear()}`;
    var time = this.reservation?.time;
    var experience = this.experience?.details.titles.find(t => t.languageIsoCode.toLowerCase() == locale().locale)?.text || '';
    var cantina = this.companyInt?.name;
    return this.formatString(
      messageOrig,
      name,
      outputDateStr,
      time,
      cantina,
      reservationId,
      experience,
      this.paymentLink.url,
      this.companyInt.company_email,
      this.companyInt.phone
    );
  }

  formatString(template: string, ...values: any[]): string {
    return template.replace(/{(\d+)}/g, (match, index) => {
      const adjustedIndex = parseInt(index) - 1;
      const value = values[adjustedIndex];
      return value !== undefined ? value : match;
    });
  }
}
